import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ReportIcon from "@mui/icons-material/Flag";
import SearchIcon from "@mui/icons-material/Search";
import UploadIcon from "@mui/icons-material/Upload";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    LinearProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { toast } from "sonner";
import { removeCard, uploadBulkCard, uploadVideosCard } from "../../api/cards.service";
import { fetchCards } from "../../app/cards/cards.slice";
import { fetchAllDecks } from "../../app/catalog/catalog.slice";
import CardPreview from "../../components/CardPreview";
import SearchInput from "../../components/SearchInput";
import AddBugModal from "../BugsReports/components/AddBugModal";

const Cards = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user, cards, catalog } = useSelector((state) => state);
  const history = useHistory();

  const [visibleModalAddBug, setVisibleModalAddBug] = useState(false);
  const [cardSelectedForReport, setcardSelectedForReport] = useState("");
  const [cardSelected, setCardSelected] = useState("");
  const [isModalBulkVideo, setIsModalBulkVideo] = useState(false);
  const [isModalUploadCSV, setIsModalUploadCSV] = useState(false);
  const [deckSelected, setDeckSelected] = useState(null);
  const [csv, setCsv] = useState("");
  const [videos, setVideos] = useState([]);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [errors, setErrors] = useState([]);
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!catalog?.allDecks || catalog?.allDecks?.length === 0) {
      dispatch(fetchAllDecks());
    }
  }, [dispatch, catalog?.allDecks]);

  useEffect(() => {
    // If cards were already preloaded at boot, skip initial fetch
    if (!cards?.value || cards?.value?.length === 0) {
      handleSearchCard("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (errors?.length === videos?.length && videos.length > 0) {
      setLoadingUpload(false);
    }
  }, [errors, videos]);

  const handleSearchCard = (e) => {
    setSearch(e);
    dispatch(fetchCards({ take: 100, skip: 0, search: e }));
  };

  const handleDeleteCard = async () => {
    let response = await removeCard(cardSelected?._id);
    if (response?.data === true) {
      toast.success(t("toast.cardDeleted"));
      dispatch(fetchCards({ take: 100, skip: 0 }));
      setTimeout(() => setIsDeleteModal(false), 500);
    } else {
      toast.error(t("toast.errorOccurred"));
    }
  };

  const handleUploadCSV = (e) => {
    if (e.target.files?.[0]?.type?.includes("csv")) {
      setCsv(e.target.files?.[0]);
    } else {
      toast.error(t("toast.onlyCsvFile"));
    }
  };

  const handleUploadBulkCards = async () => {
    setLoadingUpload(true);
    let body = new FormData();
    body.append("file", csv);
    if (deckSelected?.value) {
      body.append("deck", deckSelected?.value);
    }
    let uploaded = await uploadBulkCard(body);
    if (uploaded?.status === 200) {
      setLoadingUpload(false);
      toast.success(t("toast.cardsUploaded"));
      var blob = new Blob([uploaded?.data], { type: "text/csv" });
      var objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl);
      setCsv("");
      setIsModalUploadCSV(false);
    } else {
      setLoadingUpload(false);
      toast.error(t("toast.errorUploadingCards"));
    }
  };

  const handleUploadFile = (e) => {
    setVideos([...e.target.files]);
  };

  const handleRemoveIndex = (index) => {
    setVideos((videos) => videos.filter((_, i) => i !== index));
  };

  const handleUploadVideos = async () => {
    setLoadingUpload(true);
    let count = 0;
    async function uploadFile(file) {
      let body = new FormData();
      body.append("file", file);
      let uploaded = await uploadVideosCard(body);
      if (uploaded?.status === 200 || uploaded?.status === 201) {
        setErrors((errors) => [...errors, { status: true }]);
      } else {
        setErrors((errors) => [...errors, { status: false, message: uploaded?.response?.data?.message }]);
      }
      if (videos?.[count + 1]) {
        count = count + 1;
        uploadFile(videos?.[count]);
      }
    }
    if (videos?.length > 0) uploadFile(videos?.[0]);
  };

  const handleRemoveVideos = () => {
    setVideos([]);
    setErrors([]);
  };

  const deckOptions = (catalog?.allDecks || []).map((deck) => ({ label: deck?.title, value: deck?._id }));

  const cardList = cards?.value || [];

  return (
    <Box sx={{ display: "flex", gap: 2, height: "100%" }}>
      <AddBugModal visible={visibleModalAddBug} setVisible={setVisibleModalAddBug} selectedCardProp={cardSelectedForReport} />

      {/* Main table */}
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Paper sx={{ borderRadius: 1, overflow: "hidden", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          <Box sx={{ p: 3, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="h6" fontWeight={700}>{t("pages.cards")}</Typography>
              <Chip label={cardList.length} size="small" sx={{ fontWeight: 700, fontSize: 13 }} />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              {user?.value?.role !== "TEACHER" && (
                <Stack direction="row" spacing={2} sx={{ flexShrink: 0, alignItems: "center" }}>
                  <Button 
                    variant="contained" 
                    startIcon={<AddIcon />} 
                    onClick={() => history.push("/card/add")}
                    disableElevation
                    sx={{ 
                      whiteSpace: "nowrap", 
                      minWidth: "140px",
                      py: 1,
                      px: 2.5,
                      borderRadius: "10px", 
                      textTransform: "none", 
                      fontWeight: 600,
                      fontSize: "14px",
                      background: "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.15) 100%)",
                      border: "1px solid rgba(16, 185, 129, 0.2)",
                      color: "#10b981",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                        color: "#ffffff",
                        borderColor: "transparent",
                        boxShadow: "0 4px 14px rgba(16, 185, 129, 0.35)",
                        transform: "translateY(-1px)",
                      }
                    }}
                  >
                    {t("pages.newCard")}
                  </Button>
                  <Button 
                    variant="contained" 
                    startIcon={<VideoLibraryIcon />} 
                    onClick={() => setIsModalBulkVideo(true)}
                    disableElevation
                    sx={{ 
                      whiteSpace: "nowrap", 
                      minWidth: "140px",
                      py: 1,
                      px: 2.5,
                      borderRadius: "10px", 
                      textTransform: "none", 
                      fontWeight: 600,
                      fontSize: "14px",
                      background: "linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(79, 70, 229, 0.15) 100%)",
                      border: "1px solid rgba(99, 102, 241, 0.2)",
                      color: "#818cf8",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                        color: "#ffffff",
                        borderColor: "transparent",
                        boxShadow: "0 4px 14px rgba(79, 70, 229, 0.35)",
                        transform: "translateY(-1px)",
                      }
                    }}
                  >
                    Bulk Videos
                  </Button>
                  {user?.value?.role === "ADMIN" && (
                    <Button 
                      variant="contained" 
                      startIcon={<UploadIcon />} 
                      onClick={() => setIsModalUploadCSV(true)}
                      disableElevation
                      sx={{ 
                        whiteSpace: "nowrap", 
                        minWidth: "140px",
                        py: 1,
                        px: 2.5,
                        borderRadius: "10px", 
                        textTransform: "none", 
                        fontWeight: 600,
                        fontSize: "14px",
                        background: "linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.15) 100%)",
                        border: "1px solid rgba(59, 130, 246, 0.2)",
                        color: "#60a5fa",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                          color: "#ffffff",
                          borderColor: "transparent",
                          boxShadow: "0 4px 14px rgba(37, 99, 235, 0.35)",
                          transform: "translateY(-1px)",
                        }
                      }}
                    >
                      Bulk Cards
                    </Button>
                  )}
                </Stack>
              )}
              <SearchInput
                placeholder={t("cards.searchCard")}
                value={search}
                onChange={handleSearchCard}
                onClear={() => handleSearchCard("")}
                size="medium"
              />
            </Stack>
          </Box>
          <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", bgcolor: "background.paper" }}>{t("cards.char")}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", bgcolor: "background.paper" }}>{t("cards.pinyin")}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", bgcolor: "background.paper" }}>{t("cards.translation")}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", bgcolor: "background.paper" }} align="right">{t("common.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cardList.map((card, index) => (
                  <TableRow
                    key={card?._id || index}
                    hover
                    sx={{
                      "&:last-child td": { border: 0 },
                      cursor: "pointer",
                    }}
                  >
                    <TableCell onClick={() => setCardSelected(card)}>
                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: 16 }}>
                        {card?.meaning?.character}
                      </Typography>
                    </TableCell>
                    <TableCell onClick={() => setCardSelected(card)}>
                      <Typography variant="body2" color="text.secondary">
                        {card?.meaning?.pinyin}
                      </Typography>
                    </TableCell>
                    <TableCell onClick={() => setCardSelected(card)}>
                      <Typography variant="body2" color="text.secondary">
                        {card?.meaning?.trans}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => { setcardSelectedForReport(card); setVisibleModalAddBug(true); }}
                        >
                          <ReportIcon fontSize="small" />
                        </IconButton>
                        {user?.value?.role !== "TEACHER" && (
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => history.push(`/card/edit/${card?._id}`)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                        {user?.value?.role !== "TEACHER" && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => { setIsDeleteModal(true); setCardSelected(card); }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* Card Preview sidebar - sticky so it stays visible when scrolling */}
      <Box sx={{ width: 300, flexShrink: 0, display: { xs: "none", lg: "block" }, position: "sticky", top: 0, alignSelf: "flex-start", maxHeight: "calc(100vh - 32px)", overflow: "auto" }}>
        <CardPreview card={cardSelected} />
      </Box>

      {/* Delete Card Dialog */}
      <Dialog open={isDeleteModal} onClose={() => setIsDeleteModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t("cards.deleteCard")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete {cardSelected?.meaning?.character} ({cardSelected?.meaning?.pinyin})?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setIsDeleteModal(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" color="error" onClick={handleDeleteCard}>{t("cards.deleteCard")}</Button>
        </DialogActions>
      </Dialog>

      {/* Upload CSV Dialog */}
      <Dialog
        open={isModalUploadCSV}
        onClose={() => {
          setIsModalUploadCSV(false);
          setDeckSelected(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Upload Bulk Cards</DialogTitle>
        <DialogContent sx={{ overflow: "visible" }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>{t("common.selectDeck")}</Typography>
              <Autocomplete
                value={deckSelected}
                onChange={(_, newValue) => setDeckSelected(newValue)}
                options={deckOptions}
                getOptionLabel={(option) => option?.label ?? ""}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                loading={catalog?.isLoadingDecks}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t("catalog.searchDecks")}
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.value}>
                    {option.label}
                  </li>
                )}
                sx={{ width: "100%" }}
              />
            </Box>
            <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} fullWidth>
              Select CSV file
              <input key={csv ? `${csv.name}-${csv.lastModified}` : "empty-csv"} type="file" accept=".csv" hidden onChange={handleUploadCSV} />
            </Button>
            {csv && (
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography variant="body2">{csv?.name}</Typography>
                <Typography variant="caption" color="text.secondary">{csv?.type}</Typography>
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button disabled={!csv} color="error" onClick={() => setCsv("")}>{t("common.removeCsv")}</Button>
          <Button variant="contained" disabled={!csv || loadingUpload} onClick={handleUploadBulkCards}>
            {loadingUpload ? <CircularProgress size={20} /> : t("common.uploadCsv")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Video Dialog */}
      <Dialog open={isModalBulkVideo} onClose={() => setIsModalBulkVideo(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Upload Bulk Videos</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} fullWidth>
              Select video files
              <input key={`videos-${videos?.length || 0}`} type="file" multiple hidden onChange={handleUploadFile} />
            </Button>
            {videos?.map((c, i) => (
              <Paper variant="outlined" key={i} sx={{ p: 1.5, borderRadius: 2, display: "flex", alignItems: "center", gap: 2 }}>
                <video src={URL.createObjectURL(c)} style={{ width: 120, borderRadius: 8 }} controls />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">{c?.name}</Typography>
                  {errors?.[i]?.status === false && (
                    <Alert severity="error" sx={{ mt: 0.5, py: 0 }}>
                      {errors?.[i]?.message || t("cards.networkError")}
                    </Alert>
                  )}
                  {errors?.[i]?.status === true && (
                    <Alert severity="success" sx={{ mt: 0.5, py: 0 }}>Success</Alert>
                  )}
                </Box>
                {!errors?.[i] && (
                  <IconButton size="small" color="error" onClick={() => handleRemoveIndex(i)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Paper>
            ))}
            {loadingUpload && (
              <Box>
                <LinearProgress variant="determinate" value={(errors.length / videos.length) * 100} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {errors.length}/{videos.length} videos processed
                </Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {errors?.filter((c) => c?.status === false)?.length > 0 && (
            <Typography variant="body2" color="error" sx={{ mr: "auto" }}>
              {errors.filter((c) => c?.status === false).length} errors
            </Typography>
          )}
          <Button color="error" onClick={handleRemoveVideos}>{t("common.removeAll")}</Button>
          <Button
            variant="contained"
            disabled={videos.length === 0 || loadingUpload}
            onClick={handleUploadVideos}
          >
            {loadingUpload ? <CircularProgress size={20} /> : `Upload (${videos.length} videos)`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Cards;
